import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import Button from "@scripts/react/components/Button";

enum HintRotatingContent {
    FINDING_OBJECTS = "Finding objects...",
    FEEDING_RACCOONS = "Feeding raccoons...",
    GENERATING_MODEL = "Generating model...",
    TESTING_MODEL = "Testing model..."
}

// Abort UI configuration based on error type
export interface AbortUIConfig {
    message: string;
    subMessage: string;
    allowRetry: boolean;
}

interface BannerProgressHintProps {
    progress: number;
    rotatingContent?: string[];
    onStop?: () => void;
    onRestart?: () => void;
    isStopped?: boolean;
    isAborted?: boolean;
    abortUIConfig?: AbortUIConfig;
}

const rotatingContentDefault = [
    HintRotatingContent.FINDING_OBJECTS,
    HintRotatingContent.FEEDING_RACCOONS,
    HintRotatingContent.GENERATING_MODEL
];

const defaultAbortUIConfig: AbortUIConfig = {
    message: "We had an error processing this file.",
    subMessage: "Do you want to retry?",
    allowRetry: true
};

export const BannerProgressHint: React.FC<BannerProgressHintProps> = ({
    progress,
    rotatingContent = rotatingContentDefault,
    onStop,
    onRestart,
    isStopped = false,
    isAborted = false,
    abortUIConfig = defaultAbortUIConfig
}) => {
    const [currentTextIndex, setCurrentTextIndex] = useState(0);

    useEffect(() => {
        // Only rotate text when not stopped or aborted
        if (isStopped || isAborted) return;

        const interval = setInterval(() => {
            setCurrentTextIndex((prev) => (prev + 1) % rotatingContent.length);
        }, 3000);

        return () => clearInterval(interval);
    }, [rotatingContent.length, isStopped, isAborted]);

    // Aborted state - show error message and conditionally show retry button
    if (isAborted) {
        return (
            <motion.div className={twClassNames("flex w-96 flex-row items-center gap-4")}>
                <div className="flex w-20 items-center justify-center">
                    <i className="far fa-circle-exclamation text-2xl text-red-500" />
                </div>
                <div className="flex w-full flex-col gap-1">
                    <span className="text-xs text-red-600">{abortUIConfig.message}</span>
                    <span className="text-xs text-gray-500">{abortUIConfig.subMessage}</span>
                </div>
                {abortUIConfig.allowRetry && onRestart && (
                    <Button
                        type="tertiary"
                        size="xxsmall"
                        onClick={onRestart}
                        className="w-20 text-xs"
                    >
                        <i className="far fa-rotate-right" />
                        Retry
                    </Button>
                )}
            </motion.div>
        );
    }

    return (
        <motion.div className={twClassNames("flex w-96 flex-row items-center gap-4")}>
            <div className="w-20">
                <img
                    src={
                        isStopped
                            ? "/images/running-lenny.svg"
                            : "/images/running_lenny_outline.gif"
                    }
                    alt={isStopped ? "Paused" : "Generating model"}
                    className={twClassNames("w-auto object-contain", isStopped ? "h-7" : "h-6")}
                />
            </div>
            <div className="flex w-full flex-col gap-2">
                <div className={twClassNames("flex w-full items-center justify-between")}>
                    <span className={twClassNames(isStopped ? "text-xs" : "animate-pulse text-xs")}>
                        {rotatingContent[currentTextIndex]}
                    </span>
                    <span className="font-mono text-xs">{progress.toFixed(0)}%</span>
                </div>
                <div className="h-1 w-full overflow-hidden rounded-full bg-gray-200">
                    <motion.div
                        className="h-full rounded-full bg-gradient-to-r from-purboflow-400 to-purboflow-800"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                    />
                </div>
            </div>
            {(onStop || onRestart) && (
                <Button
                    type="tertiary"
                    size="xxsmall"
                    onClick={isStopped ? onRestart : onStop}
                    className="w-20 text-xs"
                >
                    {isStopped ? (
                        <>
                            <i className="far fa-rotate-right" />
                            Restart
                        </>
                    ) : (
                        <>
                            <i className="far fa-stop" />
                            Stop
                        </>
                    )}
                </Button>
            )}
        </motion.div>
    );
};
